import { useEffect, useMemo, useState } from 'react';
import type { GameId } from '@urbanrats/contracts';
import { promoPayout, ROUND_TERMINAL_STATES } from '@urbanrats/domain';
import { applyFilters, can, exposure, worstCaseByRound, type ExposureRow, type WorstCaseRow } from '@urbanrats/ops-core';
import { ChangeRequests } from '../components/ChangeRequests.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { Link } from '../components/Link.tsx';
import { StateTag } from '../components/common.tsx';
import { PauseControl } from '../components/PauseControl.tsx';
import { fmtAtomic, hrefWith, useCtx } from '../lib.ts';

type Sel = 'UNDER' | 'EXACT_LOW' | 'RANGE' | 'EXACT_HIGH' | 'OVER' | 'EXACT';
interface Lim {
  minStakeAtomic: string;
  maxStakeAtomic: string;
  maxLiabilityPerSelectionAtomic: string;
  maxRoundLiabilityAtomic?: string;
  maxSourceLiabilityAtomic?: string;
  payoutCap: { amountAtomic: string; currency: string; scale: number };
}
interface LimitsView {
  games: { gameId: string; title: string; version: number; paused: boolean; limits: Record<string, Lim> }[];
}
interface LiveBet {
  currency: string;
  scale: number;
  selection: string;
  n: number;
  players: number;
  stake: string;
  potential: string;
}
interface LiveRound {
  gameRoundId: string;
  state: string;
  thresholds: { low: number; high: number } | null;
  countSoFar: number | null;
  finalCount: number | null;
  camera: { city: string | null; country: string | null } | null;
  bets: LiveBet[];
}

const SELS: { sel: Sel; label: string }[] = [
  { sel: 'UNDER', label: 'Few' },
  { sel: 'EXACT_LOW', label: 'Exactly L' },
  { sel: 'RANGE', label: 'Middle' },
  { sel: 'EXACT_HIGH', label: 'Exactly H' },
  { sel: 'OVER', label: 'Many' },
  { sel: 'EXACT', label: 'Either limit' },
];

/** Selections that win at a given count (count bands). */
export function winsAt(n: number, L: number, H: number): Sel[] {
  if (n < L) return ['UNDER'];
  if (n === L) return ['EXACT_LOW', 'EXACT'];
  if (n < H) return ['RANGE'];
  if (n === H) return ['EXACT_HIGH', 'EXACT'];
  return ['OVER'];
}

interface Book {
  currency: string;
  scale: number;
  cash: bigint;
  bets: number;
  sel: Map<string, { stake: bigint; payout: bigint; n: number }>;
}

/** House result for every possible count: cash taken minus what the winning selections pay. */
function curve(book: Book, L: number, H: number, upTo: number) {
  return Array.from({ length: upTo + 1 }, (_, n) => {
    const pay = winsAt(n, L, H).reduce((a, s) => a + (book.sel.get(s)?.payout ?? 0n), 0n);
    return { n, pay, net: book.cash - pay };
  });
}

const pct = (a: bigint, b: bigint) => (b > 0n ? Number((a * 10_000n) / b) / 100 : 0);

/**
 * Risk: what the open rounds can cost us. The round on air with its payout-by-count curve and limit gauges,
 * every open round's worst case, the limits in force (changed through maker-checker) and the players with the
 * largest open potential payout. Accepted prices never change; the levers are limits (future bets) and pause.
 */
export function Risk() {
  const { data, filters, backend, user } = useCtx();
  const f = applyFilters(data, filters);
  const rows = exposure(f.rounds, f.bets);
  const worst = worstCaseByRound(f.rounds, rows, f.bets);
  const games = [...new Set([...data.games.map((g) => g.gameId)])] as GameId[];
  const [live, setLive] = useState<LiveRound | null>(null);
  const [limits, setLimits] = useState<LimitsView | null>(null);
  const [showTables, setShowTables] = useState(false);
  useEffect(() => {
    if (!backend.server) return;
    const load = () => {
      void backend.server!.get<{ current: LiveRound | null }>('/v1/admin/live/command-center').then((r) => r.ok && setLive(r.value.current));
    };
    const first = setTimeout(() => {
      load();
      void backend.server!.get<LimitsView>('/v1/admin/risk/limits').then((r) => r.ok && setLimits(r.value));
    }, 0);
    const t = setInterval(load, 3000);
    return () => (clearTimeout(first), clearInterval(t));
  }, [backend]);
  const liveLimits = limits?.games.find((g) => g.gameId === 'traffic-pulse')?.limits ?? null;

  // Players with the largest potential payout across open rounds (cash bets, accepted quotes).
  const topPlayers = (() => {
    const m = new Map<string, { player: string; operator: string; currency: string; scale: number; bets: number; stake: bigint; potential: bigint }>();
    for (const b of f.bets) {
      const r = f.rounds.find((x) => x.gameRoundId === b.bet.gameRoundId);
      if (!r || ROUND_TERMINAL_STATES.has(r.state) || b.bet.state !== 'ACCEPTED' || !b.bet.quote) continue;
      const q = b.bet.quote;
      const funding = b.bet.funding === 'FREEBET' && q.freebetPolicy ? ({ kind: 'FREEBET', faceValue: b.bet.stake, policy: q.freebetPolicy } as const) : ({ kind: 'CASH' } as const);
      const pay = BigInt(promoPayout({ stake: b.bet.stake, odds: q.odds, lightning: q.lightning, boostBps: q.boostBps, cap: q.payoutCap, funding }, true).credit.amountAtomic);
      const k = `${b.playerRef}|${b.bet.stake.currency}`;
      const cur = m.get(k) ?? { player: b.playerRef, operator: b.operatorId, currency: b.bet.stake.currency, scale: b.bet.stake.scale, bets: 0, stake: 0n, potential: 0n };
      cur.bets++;
      cur.stake += BigInt(b.bet.stake.amountAtomic);
      cur.potential += pay;
      m.set(k, cur);
    }
    return [...m.values()].sort((a, b) => (b.potential > a.potential ? 1 : -1)).slice(0, 8);
  })();

  return (
    <div className="risk" data-testid="risk-page">
      <div className="cc-head">
        <div>
          <h1>Risk</h1>
          <p className="note">What open rounds can cost us, per currency. Accepted prices never change: the levers are the limits (for new bets) and pausing new rounds.</p>
        </div>
      </div>

      {backend.server && live && live.thresholds && <OnAirRisk round={live} limits={liveLimits} />}

      <section className="card-x" data-testid="worst-case">
        <header className="card-h">
          <h2>Open rounds · worst case</h2>
          <span className="note">payouts of selections that win together are added (e.g. Exactly L + Either limit)</span>
        </header>
        {worst.length === 0 ? (
          <p className="muted">No open exposure: no accepted bets in unresolved rounds.</p>
        ) : (
          <div className="worst-cards">
            {worst.map((w) => (
              <WorstCard key={`${w.gameRoundId}|${w.currency}`} w={w} rows={rows.filter((r) => r.gameRoundId === w.gameRoundId && r.currency === w.currency)} limit={limits?.games.find((g) => g.gameId === w.gameId)?.limits[w.currency] ?? null} />
            ))}
          </div>
        )}
        <button type="button" className="linkish" onClick={() => setShowTables(!showTables)}>
          {showTables ? 'Hide' : 'Show'} per-selection table
        </button>
        {showTables && (
          <DataTable<ExposureRow>
            testId="exposure-table"
            rows={rows}
            rowKey={(r) => `${r.gameRoundId}|${r.currency}|${r.selection}`}
            empty="No open exposure."
            exportName="exposure"
            columns={[
              { key: 'round', header: 'Round', cell: (r) => <Link to={hrefWith(`/rounds/${r.gameRoundId}`, filters)}>{r.gameRoundId}</Link>, csv: (r) => r.gameRoundId },
              { key: 'state', header: 'State', cell: (r) => <StateTag state={r.state} />, csv: (r) => r.state },
              { key: 'sel', header: 'If this wins', cell: (r) => r.selection, csv: (r) => r.selection },
              { key: 'bets', header: 'Bets', numeric: true, cell: (r) => r.bets, csv: (r) => r.bets },
              { key: 'stake', header: 'Stake on it', numeric: true, cell: (r) => fmtAtomic(r.stake, r.scale, r.currency), csv: (r) => fmtAtomic(r.stake, r.scale, r.currency) },
              { key: 'pay', header: 'Payout if wins', numeric: true, cell: (r) => fmtAtomic(r.payoutIfWins, r.scale, r.currency), csv: (r) => fmtAtomic(r.payoutIfWins, r.scale, r.currency) },
              { key: 'net', header: 'Net vs round cash', numeric: true, cell: (r) => <span className={r.netIfWins > 0n ? 'bad-text' : ''}>{fmtAtomic(r.netIfWins, r.scale, r.currency)}</span>, csv: (r) => fmtAtomic(r.netIfWins, r.scale, r.currency) },
            ]}
          />
        )}
      </section>

      <div className="voids-grid">
        <section className="card-x" data-testid="top-players">
          <header className="card-h">
            <h2>Largest open potential payouts</h2>
            <span className="note">per player and currency, accepted bets in open rounds</span>
          </header>
          {topPlayers.length === 0 ? (
            <p className="muted">No open bets.</p>
          ) : (
            <ul className="reason-list">
              {topPlayers.map((p) => (
                <li key={`${p.player}|${p.currency}`}>
                  <Link to={`/players/${encodeURIComponent(p.player)}`} className="mono">
                    {p.player.slice(0, 14)}
                  </Link>
                  <span className="muted">
                    {p.operator} · {p.bets} bets · stake {fmtAtomic(p.stake, p.scale, p.currency)}
                  </span>
                  <strong>up to {fmtAtomic(p.potential, p.scale, p.currency)}</strong>
                </li>
              ))}
            </ul>
          )}
        </section>
        <section className="card-x">
          <header className="card-h">
            <h2>Stop new rounds</h2>
          </header>
          <GameSwitches games={games} />
        </section>
      </div>

      {backend.server && limits && <LimitsManager view={limits} canRequest={can(user, 'request.config')} />}
    </div>
  );
}

function GameSwitches({ games }: { games: GameId[] }) {
  const { data } = useCtx();
  const [open, setOpen] = useState<GameId | null>(null);
  return (
    <ul className="switch-list">
      {games.map((id) => {
        const g = data.games.find((x) => x.gameId === id);
        return (
          <li key={id}>
            <div className="switch-row">
              <strong>{g?.title ?? id}</strong>
              <span className={`pill ${g?.paused ? 'warn' : 'live'}`}>{g?.paused ? 'new rounds paused' : 'running'}</span>
              <button type="button" className="btn ghost" onClick={() => setOpen(open === id ? null : id)}>
                {open === id ? 'Close' : g?.paused ? 'Resume…' : 'Pause…'}
              </button>
            </div>
            {open === id && <PauseControl gameId={id} />}
          </li>
        );
      })}
    </ul>
  );
}

function OnAirRisk({ round, limits }: { round: LiveRound; limits: Record<string, Lim> | null }) {
  const L = round.thresholds!.low;
  const H = round.thresholds!.high;
  const books = useMemo(() => {
    const m = new Map<string, Book>();
    for (const b of round.bets) {
      const book = m.get(b.currency) ?? { currency: b.currency, scale: b.scale, cash: 0n, bets: 0, sel: new Map() };
      book.cash += BigInt(b.stake);
      book.bets += b.n;
      book.sel.set(b.selection, { stake: BigInt(b.stake), payout: BigInt(b.potential), n: b.n });
      m.set(b.currency, book);
    }
    return [...m.values()];
  }, [round]);
  const count = round.finalCount ?? round.countSoFar;
  const upTo = Math.max(H + Math.max(8, H - L + 4), (count ?? 0) + 3);
  return (
    <section className="card-x on-air-risk" data-testid="on-air-risk">
      <header className="card-h">
        <h2>
          {!['SETTLED', 'VOIDED'].includes(round.state) ? <><span className="live-dot" aria-hidden /> Round on air</> : 'Latest live round'} · {round.gameRoundId}
        </h2>
        <span className={`tag st-${round.state}`}>{round.state}</span>
        <span className="note">
          {round.camera?.city}, {round.camera?.country} · bands {L} / {H} · count {count ?? '—'}
        </span>
      </header>
      {books.length === 0 ? (
        <p className="muted">No bets on this round yet.</p>
      ) : (
        books.map((book) => {
          const pts = curve(book, L, H, upTo);
          const worst = pts.reduce((a, p) => (p.pay > a.pay ? p : a), pts[0]!);
          const lim = limits?.[book.currency];
          const roundLimit = lim?.maxRoundLiabilityAtomic ? BigInt(lim.maxRoundLiabilityAtomic) : null;
          const selLimit = lim ? BigInt(lim.maxLiabilityPerSelectionAtomic) : null;
          const fmt = (v: bigint) => fmtAtomic(v, book.scale, book.currency);
          return (
            <div key={book.currency} className="risk-book">
              <div className="risk-gauges">
                <Gauge label="Worst outcome pays" value={worst.pay} limit={roundLimit} fmt={fmt} sub={`at count ${worst.n} (${winsAt(worst.n, L, H).join(' + ')}) · ${book.bets} bets, ${fmt(book.cash)} taken`} />
                <div className="gauge-box">
                  <span className="tile-label">House result if the worst happens</span>
                  <strong className={`tile-value ${worst.net < 0n ? 'bad-text' : 'ok-text'}`}>{fmt(worst.net)}</strong>
                  <span className="tile-sub">cash taken − worst payout</span>
                </div>
              </div>
              <CountChart pts={pts} L={L} H={H} count={count} fmt={fmt} />
              <div className="sel-fill">
                {SELS.map(({ sel, label }) => {
                  const s = book.sel.get(sel);
                  const fill = s && selLimit ? pct(s.payout, selLimit) : 0;
                  return (
                    <div key={sel} className={`sf ${fill >= 100 ? 'full' : fill >= 80 ? 'warn' : ''}`}>
                      <span className="sf-name">{label}</span>
                      <span className="sf-bar" title={selLimit ? `${fill.toFixed(1)} % of the per-selection limit ${fmt(selLimit)}` : ''}>
                        <span style={{ width: `${Math.min(100, fill)}%` }} />
                      </span>
                      <span className="sf-n">{s ? `${s.n} · ${fmt(s.stake)} → ${fmt(s.payout)}` : '—'}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })
      )}
    </section>
  );
}

function Gauge({ label, value, limit, fmt, sub }: { label: string; value: bigint; limit: bigint | null; fmt: (v: bigint) => string; sub: string }) {
  const p = limit ? pct(value, limit) : null;
  return (
    <div className="gauge-box">
      <span className="tile-label">{label}</span>
      <strong className="tile-value">{fmt(value)}</strong>
      {p !== null ? (
        <>
          <span className={`gauge ${p >= 100 ? 'full' : p >= 80 ? 'warn' : ''}`} aria-label={`${p.toFixed(1)} % of the round limit`}>
            <span style={{ width: `${Math.min(100, p)}%` }} />
            <i style={{ left: '80%' }} />
          </span>
          <span className="tile-sub">
            {p.toFixed(1)} % of the round limit {fmt(limit!)} · {sub}
          </span>
        </>
      ) : (
        <span className="tile-sub">{sub} · no round limit set (per-selection limits apply)</span>
      )}
    </div>
  );
}

/** House result for each possible count: green above zero (we win), red below (we pay out more than we took). */
function CountChart({ pts, L, H, count, fmt }: { pts: { n: number; pay: bigint; net: bigint }[]; L: number; H: number; count: number | null; fmt: (v: bigint) => string }) {
  const W = 1000;
  const Hh = 220;
  const max = pts.reduce((a, p) => (p.net > a ? p.net : -p.net > a ? -p.net : a), 1n);
  const mid = Hh / 2;
  const bw = W / pts.length;
  const y = (v: bigint) => (Number((v * 1000n) / max) / 1000) * (mid - 12);
  const band = (from: number, to: number, cls: string, label: string) => (
    <g>
      <rect x={from * bw} y={0} width={(to - from) * bw} height={Hh} className={`cc-band ${cls}`} />
      <text x={((from + to) / 2) * bw} y={14} className="cc-band-label">
        {label}
      </text>
    </g>
  );
  return (
    <div className="count-chart">
      <svg viewBox={`0 0 ${W} ${Hh + 18}`} role="img" aria-label="House result for each possible count">
        {band(0, L, 'few', 'Few')}
        {band(L, L + 1, 'exact', String(L))}
        {band(L + 1, H, 'mid', 'Middle')}
        {band(H, H + 1, 'exact', String(H))}
        {band(H + 1, pts.length, 'many', 'Many')}
        <line x1={0} x2={W} y1={mid} y2={mid} className="cc-zero" />
        {pts.map((p) => {
          const h = y(p.net);
          return (
            <rect key={p.n} x={p.n * bw + bw * 0.15} width={bw * 0.7} y={h >= 0 ? mid - h : mid} height={Math.max(1, Math.abs(h))} className={p.net >= 0n ? 'cc-pos' : 'cc-neg'}>
              <title>{`count ${p.n}: pays ${fmt(p.pay)} · house ${fmt(p.net)}`}</title>
            </rect>
          );
        })}
        {count !== null && count < pts.length && <line x1={(count + 0.5) * bw} x2={(count + 0.5) * bw} y1={0} y2={Hh} className="cc-now" />}
        {pts
          .filter((p) => p.n % 5 === 0)
          .map((p) => (
            <text key={p.n} x={(p.n + 0.5) * bw} y={Hh + 14} className="cc-axis">
              {p.n}
            </text>
          ))}
      </svg>
      <div className="phase-labels">
        <span>vehicles counted →</span>
        <span>
          <i className="sw pos" /> house wins <i className="sw neg" /> house pays more than it took {count !== null && <>· white line = count now</>}
        </span>
      </div>
    </div>
  );
}

function WorstCard({ w, rows, limit }: { w: WorstCaseRow; rows: ExposureRow[]; limit: Lim | null }) {
  const fmt = (v: bigint) => fmtAtomic(v, w.scale, w.currency);
  const roundLimit = limit?.maxRoundLiabilityAtomic ? BigInt(limit.maxRoundLiabilityAtomic) : null;
  const p = roundLimit ? pct(w.payout, roundLimit) : null;
  return (
    <Link to={`/rounds/${encodeURIComponent(w.gameRoundId)}`} className="worst-card">
      <span className="mono">{w.gameRoundId}</span>
      <span className="muted small-left">
        {w.gameId} · {rows.reduce((a, r) => a + r.bets, 0)} bets
      </span>
      <strong className={w.net > 0n ? 'bad-text' : 'ok-text'}>{fmt(-w.net)}</strong>
      <span className="tile-sub">
        house if {w.outcome.join(' + ')} wins · pays {fmt(w.payout)}
      </span>
      {p !== null && (
        <span className={`gauge ${p >= 100 ? 'full' : p >= 80 ? 'warn' : ''}`}>
          <span style={{ width: `${Math.min(100, p)}%` }} />
        </span>
      )}
    </Link>
  );
}

const FIELDS: { key: keyof Lim; label: string; help: string; optional?: boolean }[] = [
  { key: 'minStakeAtomic', label: 'Min stake', help: 'smallest bet' },
  { key: 'maxStakeAtomic', label: 'Max stake', help: 'largest single bet' },
  { key: 'maxLiabilityPerSelectionAtomic', label: 'Per selection', help: 'most we pay if one selection wins (all its bets)' },
  { key: 'maxRoundLiabilityAtomic', label: 'Per round', help: 'most we pay in the worst outcome of a round', optional: true },
  { key: 'maxSourceLiabilityAtomic', label: 'Per camera window', help: 'all games counting the same camera window together', optional: true },
];

function toUnits(atomic: string, scale: number): string {
  const v = BigInt(atomic);
  const d = 10n ** BigInt(scale);
  const frac = (v % d).toString().padStart(scale, '0').replace(/0+$/, '');
  return `${v / d}${frac ? `.${frac}` : ''}`;
}
function toAtomic(units: string, scale: number): string | null {
  const m = /^(\d+)(?:[.,](\d+))?$/.exec(units.trim());
  if (!m) return null;
  const frac = (m[2] ?? '').padEnd(scale, '0');
  if (frac.length > scale) return null;
  const v = BigInt(m[1]!) * 10n ** BigInt(scale) + BigInt(frac || '0');
  return v > 0n ? v.toString() : null;
}

/** Limits in force; a change is a maker-checker request (new config version for future bets). */
function LimitsManager({ view, canRequest }: { view: LimitsView; canRequest: boolean }) {
  const { backend, user, refresh } = useCtx();
  const [edit, setEdit] = useState<{ gameId: string; currency: string; field: keyof Lim; value: string; scale: number } | null>(null);
  const [reason, setReason] = useState('');
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const live = view.games.filter((g) => g.gameId === 'traffic-pulse').concat(view.games.filter((g) => g.gameId !== 'traffic-pulse'));
  const submit = async () => {
    if (!edit) return;
    const atomic = edit.value.trim() === '' ? '' : toAtomic(edit.value, edit.scale);
    if (atomic === null) return setMsg({ ok: false, text: 'Enter an amount like 250 or 12.50' });
    const f = FIELDS.find((x) => x.key === edit.field)!;
    const r = await backend.request(user, 'CONFIG_CHANGE', `${edit.gameId}/${edit.currency}`, `${edit.gameId} ${edit.currency}: ${f.label} → ${atomic === '' ? 'removed' : `${edit.value} ${edit.currency}`}`, { setting: 'LIMITS', gameId: edit.gameId, currency: edit.currency, field: edit.field, value: atomic }, reason);
    setMsg(r.ok ? { ok: true, text: `Request ${r.value.requestId} created — a checker (finance or admin) approves it below.` } : { ok: false, text: `${r.error.code}: ${r.error.message}` });
    if (r.ok) {
      setEdit(null);
      setReason('');
    }
    refresh();
  };
  return (
    <section className="card-x" data-testid="limits">
      <header className="card-h">
        <h2>Limits in force</h2>
        <span className="note">checked by the server for every bet, per currency · changes need a second approver and apply to new bets only</span>
      </header>
      {live.map((g) => (
        <div key={g.gameId} className="limit-game">
          <h3>
            {g.title} <span className="muted">· config v{g.version}</span>
          </h3>
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Currency</th>
                  {FIELDS.map((f) => (
                    <th key={f.key} className="num" title={f.help}>
                      {f.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Object.entries(g.limits).map(([cur, l]) => (
                  <tr key={cur}>
                    <td data-label="Currency">{cur}</td>
                    {FIELDS.map((f) => {
                      const v = l[f.key] as string | undefined;
                      return (
                        <td key={f.key} data-label={f.label} className="num">
                          {canRequest ? (
                            <button type="button" className="linkish" onClick={() => (setEdit({ gameId: g.gameId, currency: cur, field: f.key, value: v ? toUnits(v, l.payoutCap.scale) : '', scale: l.payoutCap.scale }), setMsg(null))} title={`Change: ${f.help}`}>
                              {v ? toUnits(v, l.payoutCap.scale) : f.optional ? 'not set' : '—'}
                            </button>
                          ) : v ? (
                            toUnits(v, l.payoutCap.scale)
                          ) : (
                            'not set'
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {edit && (
        <div className="limit-edit" data-testid="limit-edit">
          <strong>
            {edit.gameId} · {edit.currency} · {FIELDS.find((f) => f.key === edit.field)!.label}
          </strong>
          <span className="note">{FIELDS.find((f) => f.key === edit.field)!.help}</span>
          <div className="refund-form">
            <input value={edit.value} onChange={(e) => setEdit({ ...edit, value: e.target.value })} placeholder={FIELDS.find((f) => f.key === edit.field)!.optional ? 'amount (empty = remove)' : 'amount'} aria-label="New value" inputMode="decimal" />
            <span className="muted">{edit.currency}</span>
            <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (audit), e.g. demo week: lower round exposure" aria-label="Reason" />
            <button type="button" className="btn primary" disabled={reason.trim().length < 10} onClick={() => void submit()} data-testid="limit-request">
              Request change
            </button>
            <button type="button" className="btn ghost" onClick={() => setEdit(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}
      {msg && <p className={msg.ok ? 'ok-box' : 'warn-box'}>{msg.text}</p>}
      <ChangeRequests filter={(q) => q.kind === 'CONFIG_CHANGE' && q.payload.setting === 'LIMITS'} testId="limit-requests" />
    </section>
  );
}
